/**
 * DraftPreviewPage — public shareable preview page for draft prospects.
 *
 * Route: /preview/:previewToken
 *
 * Same ScreenStack composition as PublicBusinessPage Case 1.
 * Booking chrome comes from BookingFlowShell via PreviewBookingFlow.
 *
 * No Stripe calls, booking writes, availability, or Turnstile from this path.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { FlowLinkInBio } from '@/components/booking-flow/FlowLinkInBio';
import { ScreenStack } from '@/components/booking-flow/FlowScaffold';
import { flowThemeVars, FLOW_DEFAULTS } from '@/components/booking-flow/themes';
import { buildThemeFromDraft } from '@/components/draft-preview/buildThemeFromDraft';
import PreviewBookingFlow from '@/components/draft-preview/PreviewBookingFlow';
import { getDraftByPreviewToken } from '@/services/draftService';
import { ProviderPageSkeleton } from '@/components/ui/skeleton-block';
import NotFound from '@/pages/NotFound';
import type { DraftPublicView } from '@/types/draft';

/** Dev-only Mayara fixture for local visual QA */
const DEMO_DRAFT: DraftPublicView = {
  id: 'demo-mayara-brand',
  business_name: 'Beige Boy Barbershop',
  provider_name: 'Mayara Brand',
  category: 'Barber',
  city: 'Austin, TX',
  bio: 'Precision fades, textured cuts, and sharp lineups. Specializing in modern men\'s styles with old-school attention to detail.',
  hero_headline: 'Your look, elevated.',
  hero_subheadline: null,
  cta_label: 'Book with Mayara',
  profile_image_url: 'https://images.unsplash.com/photo-1621605815971-fbc98d665033?w=200&h=200&fit=crop&crop=face',
  brand_primary_color: '#2C2C2C',
  brand_secondary_color: '#3D3D3D',
  brand_accent_color: '#C9A96E',
  instagram_url: 'https://instagram.com/mayarabrand',
  booking_url: 'https://getsquire.com/discover/barbershop/beige-boy-barbershop-austin/barber/mayara-brand',
  website_url: null,
  services: [
    { id: '1', name: 'Haircut', description: 'Classic or modern cut tailored to your style', price: 45, duration: 30 },
    { id: '2', name: 'Haircut + Beard', description: 'Full cut with beard shaping and lineup', price: 60, duration: 45 },
    { id: '3', name: 'Skin Fade', description: 'Precision skin fade with sharp lineup', price: 50, duration: 35 },
    { id: '4', name: 'Beard Trim & Shape', description: 'Sculpted beard trim with hot towel finish', price: 25, duration: 20 },
    { id: '5', name: 'Kids Cut (12 & under)', description: 'Haircut for the young kings', price: 30, duration: 25 },
  ],
  custom_links: [
    { id: 'cl-1', title: 'Gallery', url: 'https://instagram.com/mayarabrand' },
    { id: 'cl-2', title: 'Beige Boy Barbershop', url: 'https://getsquire.com/discover/barbershop/beige-boy-barbershop-austin' },
  ],
  extracted_images: [],
};

type PreviewView = 'links' | 'booking';

export default function DraftPreviewPage() {
  const { previewToken } = useParams<{ previewToken: string }>();
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<DraftPublicView | null>(null);
  const [view, setView] = useState<PreviewView>('links');
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!previewToken) {
      setLoading(false);
      return;
    }

    // Dev-only fixture — never serve in production builds
    if (previewToken === 'demo') {
      if (import.meta.env.DEV) {
        setDraft(DEMO_DRAFT);
        document.title = `${DEMO_DRAFT.provider_name} | Figgy`;
      }
      setLoading(false);
      return;
    }

    async function load() {
      try {
        const data = await getDraftByPreviewToken(previewToken!);
        setDraft(data);
        if (data) {
          const displayName = data.provider_name || data.business_name || 'Preview';
          document.title = `${displayName} | Figgy`;
        }
      } catch (err) {
        console.error('Failed to load preview:', err);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    }

    load();

    return () => {
      document.title = 'Figgy - Effortless Client Bookings for Solo Professionals';
    };
  }, [previewToken]);

  const openBooking = useCallback(() => setView('booking'), []);
  const exitBooking = useCallback(() => setView('links'), []);

  if (loading) return <ProviderPageSkeleton />;
  if (loadError || !draft) return <NotFound />;

  const theme = buildThemeFromDraft(draft);

  const socialLinks: Array<{ platform: string; url: string }> = [];
  if (draft.instagram_url) socialLinks.push({ platform: 'instagram', url: draft.instagram_url });
  if (draft.website_url) socialLinks.push({ platform: 'globe', url: draft.website_url });

  const customLinks = (draft.custom_links || []).map((l) => ({
    id: l.id,
    title: l.title,
    url: l.url,
    meta: l.meta,
  }));
  if (draft.booking_url) {
    customLinks.unshift({
      id: 'existing-booking',
      title: 'Current booking page',
      url: draft.booking_url,
      meta: undefined,
    });
  }

  let handle: string | undefined;
  if (draft.instagram_url) {
    const match = draft.instagram_url.match(/instagram\.com\/([^/?]+)/);
    if (match) handle = `@${match[1]}`;
  }

  const displayName = draft.provider_name || draft.business_name || 'Provider';
  const hasServices = draft.services && draft.services.length > 0;

  return (
    <div
      style={{
        position: 'relative',
        height: '100dvh',
        overflow: 'hidden',
        background: theme.flowBg || FLOW_DEFAULTS.bg,
        ...flowThemeVars(theme),
      } as React.CSSProperties}
    >
      <ScreenStack
        screenKey={view}
        order={view === 'links' ? 0 : 1}
        noSnapshotKeys={['booking']}
      >
        {view === 'links' ? (
          <FlowLinkInBio
            theme={theme}
            businessName={draft.business_name || displayName}
            displayName={displayName}
            handle={handle}
            bio={draft.bio || draft.category || undefined}
            logoUrl={draft.profile_image_url}
            socialLinks={socialLinks}
            customLinks={customLinks}
            onBook={openBooking}
            showBookButton={hasServices}
            bookButtonLabel={draft.cta_label || 'Book appointment'}
          />
        ) : (
          <div id="preview-booking" style={{ position: 'absolute', inset: 0 }}>
            <PreviewBookingFlow
              theme={theme}
              businessName={draft.business_name || displayName}
              providerName={displayName}
              services={draft.services || []}
              onBack={exitBooking}
            />
          </div>
        )}
      </ScreenStack>
    </div>
  );
}
